import { useLayout, useMounted } from '@websolutespa/bom-mixer-hooks';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { Toaster } from 'sonner';
import { LlmInstance } from '../types';
import { useLlm } from '../useLlm/useLlm';
import { useLlmView } from '../useLlm/useLlmView';

type LlmProps = {
};

export const LlmComponent = forwardRef<LlmInstance, LlmProps>((props: LlmProps, ref) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const mounted = useMounted();
  const { locale } = useLayout();
  const {
    Root,
    PageRoot,
  } = useLlm(state => state.components);
  const { init, send, clear } = useLlm(state => state.actions);
  const ready = useLlm(state => state.ready);
  const app = useLlm(state => state.app);
  const theme = useLlm(state => state.theme);
  const embed = useLlm(state => state.embed);
  const { initView, initIntersection, open, isOpened, setOpened } = useLlmView(state => state.actions);
  const skipCustomIntro = useLlmView(state => state.skipCustomIntro);
  const idle = useLlmView(state => state.idle);
  const opened = useLlmView(state => state.opened);
  const introed = useLlmView(state => state.introed);
  const embedded = useLlmView(state => state.embedded);
  const imperative = useLlmView(state => state.imperative);

  const instance = useMemo(() => ({
    open: () => {
      open();
    },
    isOpened: () => {
      return isOpened();
    },
    setOpened: (opened: boolean) => {
      setOpened(opened);
    },
    send: (message: string) => {
      setOpened(true);
      send(message);
    },
    clear: () => {
      clear();
    },
  }), [open, isOpened, setOpened, send, clear]);

  useImperativeHandle(ref, () => instance);

  useEffect(() => {
    init(locale, instance);
    initView();
  }, [init, locale, instance, initView]);

  useEffect(() => {
    if (mounted && ready && innerRef.current) {
      initIntersection(innerRef.current);
    }
  }, [mounted, ready, initIntersection, innerRef]);

  useEffect(() => {
    if (!embedded && theme.mode !== 'page') {
      document.documentElement.style.overflow = opened ? 'hidden' : 'auto';
    }
  }, [opened, embedded, theme.mode]);

  const shouldShowTrigger = (!opened && !idle && !embed && !imperative) || Boolean(app?.contents.customIntro && !introed);
  const mode = theme.mode || 'default';

  const rootProps = useMemo(() => ({
    opened,
    embedded,
    customIntro: Boolean(app?.contents.customIntro && !skipCustomIntro),
    shouldShowTrigger,
    mode: mode,
  }), [opened, embedded, app?.contents.customIntro, skipCustomIntro, shouldShowTrigger, mode]);

  // console.log(theme, mode, app?.contents?.customTheme);

  if (mounted && ready) {
    if (mode === 'page') {
      return (
        <>
          <PageRoot ref={innerRef} {...rootProps} />
          <Toaster theme={theme.toaster} />
        </>
      );
    } else {
      return (
        <>
          <Root ref={innerRef} {...rootProps} />
          <Toaster theme={theme.toaster} />
        </>
      );
    }
  } else {
    return <></>;
  }
});

LlmComponent.displayName = 'LlmComponent';
